import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = ["Music Production", "Visual Direction", "Scoring", "Collaboration", "Other"];

const inputCls =
  "w-full bg-transparent border-b border-cream/20 focus:border-gold outline-none py-3.5 text-cream placeholder:text-cream/30 transition-colors duration-300 text-base";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: INQUIRY_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received — the studio will be in touch.");
      setForm({ name: "", email: "", inquiry_type: INQUIRY_TYPES[0], message: "" });
    } catch (err) {
      toast.error("Signal lost — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-surface border-t border-cream/10 py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow index="05" label="Contact & Connect" />
          </Reveal>
          <h2 className="mt-10 font-serif font-light text-cream text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            <MaskedLine delay={0.05}>Inquiries &</MaskedLine>
            <MaskedLine delay={0.15}>
              <em className="italic text-amber">Collaborations</em>
            </MaskedLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-base text-cream/60 leading-relaxed mt-7 max-w-md">
              For music production, visual direction, scoring, or artistic collaborations:
            </p>
            <a
              data-testid="contact-email-link"
              href="mailto:thexplab.llc@gmail.com"
              className="group inline-flex items-center gap-2 mt-6 font-serif italic text-2xl lg:text-3xl text-cream border-b border-amber/60 pb-1 hover:text-gold transition-colors duration-300"
            >
              thexplab.llc@gmail.com
              <ArrowUpRight size={20} className="text-amber transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-ink border border-cream/10 clip-frame p-8 lg:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-type" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                    Inquiry Type
                  </label>
                  <select
                    id="contact-type"
                    data-testid="contact-type-select"
                    value={form.inquiry_type}
                    onChange={set("inquiry_type")}
                    className={`${inputCls} cursor-pointer [&>option]:bg-ink`}
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell me about the world you want to build..."
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-10 inline-flex items-center gap-3 bg-amber text-cream font-mono text-[11px] uppercase tracking-[0.25em] px-9 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? "Transmitting..." : "Send Inquiry"}
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
